import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { displayText } from "@/lib/displayText";
import { api, API } from "@/lib/api";
import { setAdminFlag } from "@/lib/clarity";
import { PROOF_L, fmtDual, fmtDuration, downloadProofPDF, CGV_URL } from "@/lib/proofPdf";
import QRCode from "qrcode";
import { Logo } from "@/components/Logo";
import { AdminStats } from "@/components/AdminStats";
import { useNavigate } from "react-router-dom";

const STATUS_LABEL = { en_attente: "En attente", approuvé: "Approuvé", refusé: "Refusé" };
const STATUS_CLASS = { en_attente: "pending", approuvé: "paid", refusé: "refused" };

const SPACE_EV_LABELS = {
  onboarding_view: "écran d'accueil vu",
  business_open: "business ouvert",
  action_done: "action faite",
  action_undone: "action décochée",
  template_copied: "modèle copié",
  stuck_click: "clic « Je suis bloqué »",
  first_client: "premier client déclaré",
  regenerate: "régénération",
};

const SECTION_TITLE = { fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase", color: "#888", fontWeight: 600, marginBottom: 8, borderBottom: "1px solid #eee", paddingBottom: 4 };
const ROW = { margin: "4px 0", color: "#111" };
const CONSENT_BOX = { background: "#f6f7f9", border: "1px solid #e5e5e5", borderRadius: 8, padding: "10px 14px", marginTop: 8 };
const CONSENT_TEXT = { margin: "6px 0 0", color: "#333", fontSize: 14 };
const CHECK = { color: "#15803d", fontWeight: 600 };
const fmtBr = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Europe/Brussels" }) : "—");

export default function Admin() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("dashboard");
  const [whopLogs, setWhopLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [resetting, setResetting] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantPlan, setGrantPlan] = useState("lancement");
  const [granting, setGranting] = useState(false);
  const [grantMsg, setGrantMsg] = useState("");
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [podEmail, setPodEmail] = useState("");
  const [pod, setPod] = useState(null);
  const [podLoading, setPodLoading] = useState(false);
  const [podLang, setPodLang] = useState("fr");
  const [podQr, setPodQr] = useState(null);
  const [resendMsg, setResendMsg] = useState("");

  const resendWelcome = async () => {
    if (!pod?.found) return;
    setResendMsg("Envoi…");
    try {
      const { data } = await api.post(`/admin/resend-welcome`, { email: pod.email }, { params: { password } });
      setResendMsg(data.status === "sent" ? "Email renvoyé ✅" : "Échec de l'envoi");
      await loadProof();
    } catch (e) { setResendMsg("Échec de l'envoi"); }
  };

  useEffect(() => {
    if (pod?.found && pod.verify_path) {
      const url = window.location.origin + pod.verify_path;
      QRCode.toDataURL(url, { margin: 1, width: 240 }).then(setPodQr).catch(() => setPodQr(null));
    } else setPodQr(null);
  }, [pod]);
  const [cb, setCb] = useState(null);
  const [cbLoading, setCbLoading] = useState(false);
  const [cbQ, setCbQ] = useState("");
  const [cbRange, setCbRange] = useState("all");
  const [genBusy, setGenBusy] = useState("");

  const loadClientBusinesses = async (opts = {}) => {
    setCbLoading(true);
    try {
      if (opts.autogen) { try { await api.post(`/admin/generate-missing`, null, { params: { password } }); } catch (e) {} }
      const { data } = await api.get(`/admin/client-businesses`, { params: { password, q: cbQ.trim() || undefined, range: opts.range || cbRange } });
      setCb(data);
    } catch (e) { setCb({ error: true }); } finally { setCbLoading(false); }
  };

  const generateFor = async (client) => {
    setGenBusy(client.user_id);
    try { await api.post(`/admin/generate-business`, { user_id: client.user_id }, { params: { password } }); }
    catch (e) {} finally { setGenBusy(""); }
  };

  const openResultById = async (resultId) => {
    setViewOpen(true); setViewLoading(true); setViewData(null);
    try { const { data } = await api.get(`/admin/result/${resultId}`, { params: { password } }); setViewData(data); }
    catch (e) { setViewData({ error: true }); } finally { setViewLoading(false); }
  };

  const loadProof = async () => {
    if (!podEmail.trim()) return;
    setPodLoading(true); setPod(null);
    try {
      const { data } = await api.get(`/admin/proof-of-delivery`, { params: { email: podEmail.trim(), password } });
      setPod(data);
    } catch (e) { setPod({ error: true }); } finally { setPodLoading(false); }
  };

  const openClientResult = async (email) => {
    setViewOpen(true); setViewLoading(true); setViewData(null);
    try {
      const { data } = await api.get(`/admin/client-result`, { params: { email, password } });
      setViewData(data);
    } catch (e) { setViewData({ error: true }); } finally { setViewLoading(false); }
  };

  const loadUsers = async () => {
    try { const { data } = await api.get(`/admin/users`, { params: { password } }); setUsers(data.users); } catch (e) {}
  };

  const grantPlanToUser = async () => {
    if (!grantEmail.trim()) return;
    setGranting(true); setGrantMsg("");
    try {
      const { data } = await api.post(`/admin/grant-plan`, { email: grantEmail.trim(), plan: grantPlan }, { params: { password } });
      setGrantMsg(data.message || "Terminé.");
      if (data.found) { setGrantEmail(""); await loadUsers(); }
    } catch (e) { setGrantMsg("Erreur, réessaie."); } finally { setGranting(false); }
  };

  const addRegensToUser = async () => {
    if (!grantEmail.trim()) return;
    setGranting(true); setGrantMsg("");
    try {
      const { data } = await api.post(`/admin/add-regens`, { email: grantEmail.trim(), count: 5 }, { params: { password } });
      setGrantMsg(data.message || "Terminé.");
      if (data.found) await loadUsers();
    } catch (e) { setGrantMsg("Erreur, réessaie."); } finally { setGranting(false); }
  };

  const resetStats = async () => {
    if (!window.confirm("Réinitialiser les stats ? Cela supprime les leads, les journaux de paiement et les avis d'exemple. Les comptes utilisateurs ne sont PAS touchés.")) return;
    setResetting(true);
    try {
      await api.post(`/admin/reset-stats`, null, { params: { password } });
      const { data } = await api.get(`/admin/leads`, { params: { password } });
      setLeads(data.leads);
      await loadReviews(password);
      setWhopLogs([]);
    } catch (e) {} finally { setResetting(false); }
  };

  const loadReviews = async (pw) => {
    try { const { data } = await api.get(`/admin/reviews`, { params: { password: pw } }); setReviews(data.reviews); } catch (e) {}
  };

  const loadWhopLogs = async () => {
    try { const { data } = await api.get(`/admin/whop-logs`, { params: { password } }); setWhopLogs(data.logs); } catch (e) {}
  };

  const login = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/leads`, { params: { password } });
      setLeads(data.leads);
      await loadReviews(password);
      setAuthed(true);
      setAdminFlag(); // Stop Google Analytics for this admin browser.
    } catch (e) {
      setError("Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  const moderate = async (rid, action) => {
    try { await api.post(`/admin/reviews/${rid}/${action}`, null, { params: { password } }); await loadReviews(password); } catch (e) {}
  };

  const deleteDemo = async () => {
    try { await api.post(`/admin/reviews/delete-demo`, null, { params: { password } }); await loadReviews(password); } catch (e) {}
  };

  const exportCsv = () => {
    window.open(`${API}/admin/export?password=${encodeURIComponent(password)}`, "_blank");
  };

  if (!authed) {
    return (
      <div className="center-page">
        <div className="mb-12 w-full max-w-[480px]"><Logo onClick={() => navigate("/")} /></div>
        <motion.main initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} className="center-panel">
          <p className="eyebrow">ESPACE PRIVÉ</p>
          <h1>Admin <em>idevorai.</em></h1>
          <label className="field-label mt-8" htmlFor="admin-password">Mot de passe</label>
          <input
            id="admin-password"
            autoComplete="current-password"
            type="password"
            data-clarity-mask="true"
            data-testid="admin-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            placeholder="Mot de passe"
            className="field"
          />
          {error && <p className="error-message" role="alert" data-testid="admin-error">{error}</p>}
          <button onClick={login} disabled={loading} data-testid="admin-login" className="btn-primary w-full mt-5">
            {loading ? "Connexion en cours..." : "Se connecter →"}
          </button>
        </motion.main>
      </div>
    );
  }

  const paidCount = leads.filter((l) => l.paid).length;

  return (
    <main className="container min-h-screen pb-16">
      <header className="admin-header"><Logo onClick={() => navigate("/")} /><div className="flex flex-wrap items-center gap-3"><button onClick={resetStats} disabled={resetting} data-testid="admin-reset-stats" className="btn-secondary btn-small">{resetting ? "Réinitialisation..." : "Réinitialiser les stats"}</button><button onClick={exportCsv} data-testid="admin-export" className="btn-secondary btn-small">Exporter en CSV <span aria-hidden="true">↓</span></button></div></header>

      <div className="admin-tabs" data-testid="admin-tabs">
        <button className={`admin-tab ${tab === "dashboard" ? "active" : ""}`} onClick={() => setTab("dashboard")} data-testid="admin-tab-dashboard">Tableau de bord</button>
        <button className={`admin-tab ${tab === "users" ? "active" : ""}`} onClick={() => { setTab("users"); loadUsers(); }} data-testid="admin-tab-users">Utilisateurs</button>
        <button className={`admin-tab ${tab === "stats" ? "active" : ""}`} onClick={() => setTab("stats")} data-testid="admin-tab-stats">Statistiques</button>
        <button className={`admin-tab ${tab === "webhooks" ? "active" : ""}`} onClick={() => { setTab("webhooks"); loadWhopLogs(); }} data-testid="admin-tab-webhooks">Webhooks</button>
        <button className={`admin-tab ${tab === "proof" ? "active" : ""}`} onClick={() => setTab("proof")} data-testid="admin-tab-proof">Preuve de livraison</button>
        <button className={`admin-tab ${tab === "clients" ? "active" : ""}`} onClick={() => { setTab("clients"); loadClientBusinesses({ autogen: true }); }} data-testid="admin-tab-clients">Business clients</button>
      </div>

      {tab === "users" ? (
        <div data-testid="admin-users-panel">
          <div className="admin-heading"><div className="flex flex-wrap items-center justify-between gap-4"><div><p className="eyebrow">COMPTES</p><h1>Tous tes <em>utilisateurs.</em></h1></div><button onClick={loadUsers} data-testid="admin-users-refresh" className="btn-secondary btn-small">Rafraîchir</button></div></div>
          <div className="admin-stats">
            <div className="admin-stat"><p className="eyebrow">UTILISATEURS</p><p className="admin-stat-value" data-testid="admin-users-count">{users.length}</p></div>
            <div className="admin-stat"><p className="eyebrow">ABONNÉS</p><p className="admin-stat-value accent" data-testid="admin-users-paid">{users.filter((u) => u.paid).length}</p></div>
          </div>
          <div className="admin-grant" data-testid="admin-grant-panel">
            <p className="eyebrow">ATTRIBUER UNE OFFRE MANUELLEMENT</p>
            <p className="muted text-sm mb-3">Pour un paiement hors ligne. Débloque le résultat + crédite l'offre choisie. Le compte doit déjà exister.</p>
            <div className="flex flex-wrap items-end gap-3">
              <input type="email" placeholder="E-mail du client" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} data-testid="admin-grant-email" className="field" style={{ maxWidth: 280 }} />
              <select value={grantPlan} onChange={(e) => setGrantPlan(e.target.value)} data-testid="admin-grant-plan" className="field" style={{ maxWidth: 200 }}>
                <option value="depart">Départ</option>
                <option value="lancement">Lancement</option>
                <option value="acceleration">Accélération</option>
              </select>
              <button onClick={grantPlanToUser} disabled={granting || !grantEmail.trim()} data-testid="admin-grant-submit" className="btn-primary btn-small">{granting ? "Attribution..." : "Attribuer l'offre"}</button>
              <button onClick={addRegensToUser} disabled={granting || !grantEmail.trim()} data-testid="admin-add-regens" className="btn-secondary btn-small">+5 régénérations</button>
            </div>
            {grantMsg && <p className="muted text-sm mt-3" role="status" data-testid="admin-grant-msg">{grantMsg}</p>}
          </div>
          <div className="overflow-x-auto" data-testid="admin-users-table">
            <table className="admin-table">
              <thead><tr><th>Email</th><th>Nom</th><th>Pays</th><th>Inscription</th><th>Connexion</th><th>Source</th><th>Arrêté à</th><th>Plan</th></tr></thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={i} data-testid={`admin-user-${i}`}>
                    <td>{u.email}</td>
                    <td className="muted">{u.name || "—"}</td>
                    <td className="muted whitespace-nowrap">{u.country || "—"}</td>
                    <td className="muted whitespace-nowrap">{u.created_at ? new Date(u.created_at).toLocaleString("fr-FR") : "—"}</td>
                    <td className="muted">{u.account === false ? "Sans compte" : u.auth_provider === "google" ? "Google" : "Email"}</td>
                    <td className="muted" data-testid={`admin-user-source-${i}`}>{u.source || "—"}</td>
                    <td className="muted" data-testid={`admin-user-dropoff-${i}`}>{u.paid ? "Terminé (payé)" : u.last_question ? `Question ${u.last_question} / 21${u.last_question_label ? ` — ${u.last_question_label}` : ""}` : "—"}</td>
                    <td><span className={`status-tag ${u.paid ? "paid" : ""}`}>{u.paid ? (u.plan_name || "Payé") : "Gratuit"}</span>{u.paid && u.email && <button onClick={() => openClientResult(u.email)} data-testid={`admin-view-result-${i}`} className="btn-secondary btn-small" style={{ marginLeft: 8 }}>Voir le résultat</button>}</td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={8} className="muted" data-testid="admin-users-empty">Aucun utilisateur pour le moment.</td></tr>}
              </tbody>
            </table>
          </div>
          {viewOpen && (
            <div data-testid="admin-result-modal" onClick={() => setViewOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", justifyContent: "center", alignItems: "flex-start", overflowY: "auto", padding: "40px 16px" }}>
              <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760, width: "100%", background: "var(--paper)", borderRadius: 16, padding: 28 }}>
                <div className="flex items-center justify-between gap-4 mb-5"><p className="eyebrow">RÉSULTAT CLIENT</p><button onClick={() => setViewOpen(false)} data-testid="admin-result-close" className="btn-secondary btn-small">Fermer</button></div>
                {viewLoading ? <p className="muted">Chargement…</p> : viewData?.error ? <p className="error-message">Erreur de chargement.</p> : viewData && (
                  <>
                    <div style={{ marginBottom: 20 }} className="space-y-1">
                      <p data-testid="admin-result-email"><strong>Email :</strong> {viewData.email}</p>
                      <p data-testid="admin-result-plan"><strong>Formule :</strong> {viewData.plan_name || "—"}</p>
                      <p data-testid="admin-result-date"><strong>Date de paiement :</strong> {viewData.paid_date ? new Date(viewData.paid_date).toLocaleString("fr-FR") : "—"}</p>
                    </div>
                    {viewData.business ? (
                      <div data-testid="admin-result-content" className="space-y-5">
                        <div><p className="eyebrow">NOM DU BUSINESS</p><h2>{displayText(viewData.business.business_name)}</h2></div>
                        {viewData.business.pitch && <div><p className="eyebrow">IDÉE</p><p>{displayText(viewData.business.pitch)}</p></div>}
                        {viewData.business.target_client && <div><p className="eyebrow">CLIENT CIBLE</p><p>{displayText(viewData.business.target_client)}</p></div>}
                        {Array.isArray(viewData.business.plan_30_days) && viewData.business.plan_30_days.length > 0 && (
                          <div><p className="eyebrow">PLAN D'ACTION</p>
                            <div className="space-y-2">{viewData.business.plan_30_days.map((d, i) => (
                              <div key={i}><strong>Jour {d.day} : {displayText(d.title)}</strong>
                                <ul style={{ marginLeft: 16, listStyle: "disc" }}>{(d.tasks || []).map((t, j) => <li key={j}>{displayText(t)}</li>)}</ul>
                              </div>))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : <p className="muted" data-testid="admin-result-none">Aucun résultat enregistré</p>}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : tab === "webhooks" ? (
        <div data-testid="admin-webhooks-panel">
          <div className="admin-heading"><div className="flex flex-wrap items-center justify-between gap-4"><div><p className="eyebrow">PAIEMENTS STRIPE</p><h1>Journal des <em>webhooks.</em></h1></div><button onClick={loadWhopLogs} data-testid="admin-webhooks-refresh" className="btn-secondary btn-small">Rafraîchir</button></div></div>
          <div className="overflow-x-auto" data-testid="admin-webhooks-table">
            <table className="admin-table">
              <thead><tr><th>Date</th><th>Événement</th><th>Plan</th><th>Résultat</th></tr></thead>
              <tbody>
                {whopLogs.map((w, i) => (
                  <tr key={i} data-testid={`webhook-row-${i}`}>
                    <td className="muted whitespace-nowrap">{new Date(w.created_at).toLocaleString("fr-FR")}</td>
                    <td className="mono text-xs">{w.type}</td>
                    <td className="muted">{w.plan_key || "—"}</td>
                    <td><span className={`status-tag ${/déverrouillé|activé|Crédité/i.test(w.summary) ? "paid" : /rejetée|non identifié/i.test(w.summary) ? "refused" : ""}`}>{w.summary}</span></td>
                  </tr>
                ))}
                {whopLogs.length === 0 && <tr><td colSpan={4} className="muted" data-testid="admin-webhooks-empty">Aucun webhook reçu pour le moment.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      ) : tab === "proof" ? (
        <div data-testid="admin-proof-panel">
          <div className="admin-heading"><div><p className="eyebrow">PREUVE DE LIVRAISON</p><h1>Justificatif de <em>livraison.</em></h1></div></div>
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <input type="email" placeholder="E-mail du client" value={podEmail} onChange={(e) => setPodEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") loadProof(); }} data-testid="admin-proof-email" className="field" style={{ maxWidth: 300 }} />
            <button onClick={loadProof} disabled={podLoading || !podEmail.trim()} data-testid="admin-proof-search" className="btn-primary btn-small">{podLoading ? "Recherche..." : "Rechercher"}</button>
            <div className="flex items-center gap-1" style={{ marginLeft: "auto" }}>
              <button onClick={() => setPodLang("fr")} data-testid="proof-lang-fr" className={podLang === "fr" ? "btn-primary btn-small" : "btn-secondary btn-small"}>FR</button>
              <button onClick={() => setPodLang("en")} data-testid="proof-lang-en" className={podLang === "en" ? "btn-primary btn-small" : "btn-secondary btn-small"}>EN</button>
            </div>
            {pod?.found && <button onClick={() => downloadProofPDF(pod, podLang, window.location.origin + pod.verify_path)} data-testid="proof-download-pdf" className="btn-primary btn-small">{PROOF_L[podLang].download}</button>}
            {pod?.found && <button onClick={resendWelcome} data-testid="proof-resend-welcome" className="btn-secondary btn-small">{PROOF_L[podLang].resend}</button>}
            {resendMsg && <span className="muted text-sm" data-testid="proof-resend-msg">{resendMsg}</span>}
          </div>

          {pod?.error && <p className="error-message" data-testid="admin-proof-error">Erreur de chargement.</p>}
          {pod && pod.found === false && <p className="muted" data-testid="admin-proof-notfound">Aucun client trouvé avec cet e-mail.</p>}

          {pod && pod.found && (() => {
            const L = PROOF_L[podLang];
            const na = L.na;
            const c = pod.consent || {};
            const done = (pod.missions || []).filter((m) => m.completed);
            return (
            <div data-testid="admin-proof-sheet" style={{ background: "#ffffff", color: "#111111", borderRadius: 12, padding: "36px 40px", maxWidth: 900, margin: "0 auto", lineHeight: 1.6, boxShadow: "0 2px 20px rgba(0,0,0,0.25)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "1px solid #e5e5e5", paddingBottom: 16, marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#111" }}>ide<span style={{ color: "#7c3aed" }}>vorai</span></div>
                  <h2 style={{ margin: "8px 0 0", color: "#111", fontSize: 22 }}>{L.title}</h2>
                  <div style={{ color: "#888", fontSize: 13 }}>{L.subtitle}</div>
                </div>
                <div style={{ textAlign: "right", fontSize: 12, color: "#666" }}>
                  <div data-testid="proof-doc-number"><strong>{L.docNo}:</strong> {pod.doc_number}</div>
                  <div><strong>{L.generated}:</strong> {fmtDual(pod.generated_at, na)}</div>
                </div>
              </div>

              <section style={{ marginBottom: 20 }}>
                <div style={SECTION_TITLE}>{L.payment}</div>
                <p style={ROW}><strong>{L.email} :</strong> <span data-testid="proof-email">{pod.email || na}</span></p>
                <p style={ROW}><strong>{L.name} :</strong> {pod.name || na}</p>
                <p style={ROW}><strong>{L.plan} :</strong> <span data-testid="proof-plan">{pod.plan_name || na}</span></p>
                <p style={ROW}><strong>{L.amount} :</strong> <span data-testid="proof-amount">{pod.amount != null ? `${Number(pod.amount).toFixed(2)} ${pod.currency || "EUR"}` : na}</span></p>
                <p style={ROW}><strong>{L.paidAt} :</strong> <span data-testid="proof-paid-date">{fmtDual(pod.paid_date, na)}</span></p>
                <p style={ROW}><strong>{L.payId} :</strong> {pod.whop_payment_id || na}</p>
                <p style={ROW}><strong>{L.memId} :</strong> {pod.whop_membership_id || na}</p>
                <p style={ROW}><strong>{L.method} :</strong> {pod.payment_method || na}</p>
                <p style={ROW}><strong>{L.status} :</strong> {pod.payment_status || na}</p>
                <p style={ROW}><strong>{L.account} :</strong> {pod.whop_account || na}</p>
              </section>

              <section style={{ marginBottom: 20 }}>
                <div style={SECTION_TITLE}>{L.consent}</div>
                <div style={CONSENT_BOX} data-testid="proof-cgv">
                  <p style={{ margin: 0 }}><strong>{L.cgv}</strong> — <span style={CHECK}>{c.cgv?.accepted ? `✅ ${L.checked}` : `— ${na}`}</span>{c.cgv?.at && <span style={{ color: "#666" }}> · {L.at} {fmtDual(c.cgv.at, na)}</span>}</p>
                  <p style={CONSENT_TEXT}>{L.text} : « {c.cgv?.text || na} »</p>
                </div>
                <div style={CONSENT_BOX} data-testid="proof-waiver">
                  <p style={{ margin: 0 }}><strong>{L.waiver}</strong> — <span style={CHECK}>{c.waiver?.accepted ? `✅ ${L.checked}` : `— ${na}`}</span>{c.waiver?.at && <span style={{ color: "#666" }}> · {L.at} {fmtDual(c.waiver.at, na)}</span>}</p>
                  <p style={CONSENT_TEXT}>{L.text} : « {c.waiver?.text || na} »</p>
                </div>
                <p style={ROW}><strong>{L.ip} :</strong> {c.ip || na}</p>
                <p style={ROW}><strong>{L.device} :</strong> {c.ua || na}</p>
                <p style={ROW}><strong>{L.cgvLink} :</strong> <a href={CGV_URL} target="_blank" rel="noreferrer" style={{ color: "#7c3aed" }}>{CGV_URL}</a></p>
              </section>

              <section style={{ marginBottom: 20 }}>
                <div style={SECTION_TITLE}>{L.access}</div>
                <p style={ROW}><strong>{L.accountCreated} :</strong> <span data-testid="proof-account-created">{fmtDual(pod.account_created, na)}</span></p>
                <p style={ROW}><strong>{L.firstBienvenue} :</strong> {fmtDual(pod.bienvenue_at, na)}</p>
                <p style={ROW}><strong>{L.firstAccess} :</strong> {fmtDual(pod.first_access_at, na)}</p>
                <p style={ROW}><strong>{L.delay} :</strong> <span data-testid="proof-delay">{pod.delay_seconds != null ? L.deliveredIn.replace("{n}", pod.delay_seconds) : na}</span></p>
                <p style={ROW}><strong>{L.welcomeEmail} :</strong> <span data-testid="proof-welcome-status">{pod.welcome_email?.status === "sent" ? "✅ Envoyé" : (pod.welcome_email?.status || na)}</span>{pod.welcome_email?.to ? ` — ${pod.welcome_email.to}` : ""}</p>
                <p style={ROW}><strong>{L.sentAt} :</strong> {fmtDual(pod.welcome_email?.sent_at, na)}</p>
              </section>

              <section style={{ marginBottom: 20 }}>
                <div style={SECTION_TITLE}>{L.usage}</div>
                <p style={ROW}><strong>{L.loginCount} :</strong> <span data-testid="proof-login-count">{pod.login_count ?? 0}</span></p>
                <p style={ROW}><strong>{L.timeSpent} :</strong> {fmtDuration(pod.time_spent_seconds, L)}</p>
                <p style={ROW}><strong>{L.regens} :</strong> {pod.regen_count ?? 0}</p>
                <p style={ROW}><strong>{L.coach} :</strong> {pod.coach_messages ?? 0}</p>
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>{L.businesses} :</div>
                  {pod.businesses?.length ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }} data-testid="proof-businesses">
                      {pod.businesses.map((b, i) => <li key={i}><strong>{displayText(b.name) || "Business"}</strong> <span style={{ color: "#666" }}>— {fmtDual(b.created_at, na)}</span></li>)}
                    </ul>
                  ) : <p style={{ margin: 0, color: "#888" }} data-testid="proof-businesses-empty">{na}</p>}
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>{L.logins} :</div>
                  {pod.logins?.length ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }} data-testid="proof-logins">
                      {pod.logins.map((l, i) => <li key={i} style={{ fontSize: 13 }}>{fmtDual(l.at, na)} — IP {l.ip || na} — {(l.ua || na).slice(0, 55)}{l.kind === "register" ? ` (${L.register})` : ""}</li>)}
                    </ul>
                  ) : <p style={{ margin: 0, color: "#888" }} data-testid="proof-logins-empty">{na}</p>}
                </div>
                <div style={{ marginTop: 10 }} data-testid="proof-space-events">
                  <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>Activité dans l'espace :</div>
                  {pod.space_events && pod.space_events.total > 0 ? (
                    <>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", fontSize: 13, marginBottom: 6 }}>
                        {Object.entries(SPACE_EV_LABELS).map(([k, lbl]) => (
                          (pod.space_events.counts || {})[k] ? (
                            <span key={k}><strong>{pod.space_events.counts[k]}</strong> {lbl}</span>
                          ) : null
                        ))}
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 18 }} data-testid="proof-space-events-list">
                        {(pod.space_events.recent || []).map((e, i) => (
                          <li key={i} style={{ fontSize: 12.5, color: "#444" }}>
                            {fmtDual(e.at, na)} — {SPACE_EV_LABELS[e.type] || e.type}{e.label ? ` (${e.label})` : ""}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : <p style={{ margin: 0, color: "#888" }} data-testid="proof-space-events-empty">{na}</p>}
                </div>
              </section>

              <section style={{ marginBottom: 20 }}>
                <div style={SECTION_TITLE}>{L.missions}</div>
                <p style={ROW}><strong>{done.length} {L.missionsDone} / {(pod.missions || []).length} {L.missionsTotal}</strong></p>
                {pod.missions?.length ? (
                  <div data-testid="proof-missions" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 6 }}>
                    {pod.missions.map((m, i) => (
                      <div key={i} style={{ fontSize: 13 }}>
                        <span>{m.completed ? "✅" : "⬜"} J{m.day} : {displayText(m.title)}</span>
                        {m.completed && m.at && <span style={{ color: "#666" }}> — {fmtDual(m.at, na)}</span>}
                      </div>
                    ))}
                  </div>
                ) : <p style={{ margin: 0, color: "#888" }} data-testid="proof-missions-empty">{na}</p>}
              </section>

              <section style={{ marginBottom: 20 }}>
                <div style={SECTION_TITLE}>{L.preview}</div>
                <p style={ROW}><strong>{pod.preview?.business_name ? displayText(pod.preview.business_name) : na}</strong></p>
                {pod.preview?.excerpt && <p style={CONSENT_TEXT} data-testid="proof-preview">{displayText(pod.preview.excerpt)}</p>}
              </section>

              <section style={{ marginBottom: 20, display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap", borderTop: "1px dashed #ddd", paddingTop: 16 }}>
                <div style={{ flex: 1, minWidth: 280 }}>
                  <div style={SECTION_TITLE}>{L.integrity}</div>
                  <p style={{ margin: 0, wordBreak: "break-all", fontFamily: "monospace", fontSize: 12, color: "#333" }} data-testid="proof-hash">{pod.hash}</p>
                  <p style={{ margin: "8px 0 0" }}><a href={window.location.origin + pod.verify_path} target="_blank" rel="noreferrer" style={{ color: "#7c3aed" }} data-testid="proof-verify-link">{L.verify} →</a></p>
                </div>
                {podQr && <div style={{ textAlign: "center" }}><img src={podQr} alt="QR" width={120} height={120} data-testid="proof-qr" style={{ background: "#fff", borderRadius: 8, display: "block" }} /><span style={{ fontSize: 11, color: "#888" }}>{L.scan}</span></div>}
              </section>

              <div style={{ borderTop: "1px solid #e5e5e5", paddingTop: 16, marginTop: 20, fontSize: 13, color: "#444" }} data-testid="proof-footer">
                {podLang === "en" ? "Digital content delivered immediately after payment." : "Contenu numérique livré immédiatement après paiement."}
              </div>
            </div>
            );
          })()}
        </div>
      ) : tab === "clients" ? (
        <div data-testid="admin-clients-panel">
          <div className="admin-heading"><div className="flex flex-wrap items-center justify-between gap-4"><div><p className="eyebrow">BUSINESS CLIENTS</p><h1>Les business <em>livrés.</em></h1></div><button onClick={() => loadClientBusinesses()} data-testid="admin-clients-refresh" className="btn-secondary btn-small">Rafraîchir</button></div></div>

          {cb?.error && <p className="error-message">Erreur de chargement.</p>}

          {cb && !cb.error && (
            <>
              <div className="account-box mb-8" data-testid="admin-no-business">
                <p className="field-label">Payé sans business</p>
                <p className="account-big" data-testid="admin-no-business-count">{cb.no_business?.count ?? 0}</p>
                {cb.no_business?.count > 0 ? (
                  <div className="mt-4 space-y-2">
                    {cb.no_business.clients.map((c, i) => (
                      <div key={c.user_id} className="flex flex-wrap items-center justify-between gap-3" data-testid={`no-business-row-${i}`} style={{ borderTop: "1px solid var(--line)", paddingTop: 8 }}>
                        <div>
                          <span className="font-heading">{c.email}</span>
                          <span className="mono muted text-[10px] ml-2">{c.plan_name || "—"} · payé {fmtBr(c.paid_date)}</span>
                          {!c.has_quiz && <span className="status-tag refused ml-2" title="Pas de quiz : le client doit répondre au quiz pour générer son business">Quiz requis</span>}
                        </div>
                        {c.has_quiz ? (
                          <button onClick={() => generateFor(c)} disabled={genBusy === c.user_id} data-testid={`generate-now-${i}`} className="btn-primary btn-small">{genBusy === c.user_id ? "Lancement…" : "Générer maintenant"}</button>
                        ) : (
                          <span className="muted text-xs">En attente du quiz</span>
                        )}
                      </div>
                    ))}
                    <p className="muted text-xs mt-3">La génération des clients ayant répondu au quiz est lancée automatiquement à l'ouverture de cet onglet. Rafraîchis dans ~1 min.</p>
                  </div>
                ) : <p className="muted text-sm mt-2">Tous les clients payés ont un business. ✅</p>}
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="admin-clients-filters">
                {[["today", "Aujourd'hui"], ["last7", "7 derniers jours"], ["all", "Tout"]].map(([k, label]) => (
                  <button key={k} onClick={() => { setCbRange(k); loadClientBusinesses({ range: k }); }} data-testid={`filter-${k}`} className={cbRange === k ? "btn-primary btn-small" : "btn-secondary btn-small"}>{label}</button>
                ))}
                <input type="email" placeholder="Rechercher par email" value={cbQ} onChange={(e) => setCbQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") loadClientBusinesses(); }} data-testid="admin-clients-search" className="field" style={{ maxWidth: 260 }} />
                <button onClick={() => loadClientBusinesses()} data-testid="admin-clients-search-btn" className="btn-secondary btn-small">Rechercher</button>
              </div>

              <div className="overflow-x-auto" data-testid="admin-clients-table">
                <table className="admin-table">
                  <thead><tr><th>Email</th><th>Offre</th><th>Payé le</th><th>Business</th><th>Généré le</th><th></th></tr></thead>
                  <tbody>
                    {cbLoading && <tr><td colSpan={6} className="muted">Chargement…</td></tr>}
                    {!cbLoading && cb.businesses.map((b, i) => (
                      <tr key={b.result_id} data-testid={`client-business-row-${i}`}>
                        <td>{b.email}</td>
                        <td><span className="status-tag paid">{b.plan_name || "—"}</span></td>
                        <td className="muted whitespace-nowrap">{fmtBr(b.paid_date)}</td>
                        <td className="font-heading">{displayText(b.business_name) || "—"}{b.needs_generation && <span className="status-tag refused ml-2">À générer</span>}</td>
                        <td className="muted whitespace-nowrap">{fmtBr(b.generated_at)}</td>
                        <td><button onClick={() => openResultById(b.result_id)} data-testid={`client-business-view-${i}`} className="btn-secondary btn-small">Voir</button></td>
                      </tr>
                    ))}
                    {!cbLoading && cb.businesses.length === 0 && <tr><td colSpan={6} className="muted" data-testid="admin-clients-empty">Aucun business pour ce filtre.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {viewOpen && (
            <div data-testid="admin-result-modal-clients" onClick={() => setViewOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", justifyContent: "center", alignItems: "flex-start", overflowY: "auto", padding: "40px 16px" }}>
              <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760, width: "100%", background: "var(--paper)", borderRadius: 16, padding: 28 }}>
                <div className="flex items-center justify-between gap-4 mb-5"><p className="eyebrow">BUSINESS CLIENT</p><button onClick={() => setViewOpen(false)} data-testid="admin-result-close-clients" className="btn-secondary btn-small">Fermer</button></div>
                {viewLoading ? <p className="muted">Chargement…</p> : viewData?.error ? <p className="error-message">Erreur de chargement.</p> : viewData && (
                  <>
                    <div style={{ marginBottom: 20 }} className="space-y-1">
                      <p><strong>Email :</strong> {viewData.email}</p>
                      <p><strong>Formule :</strong> {viewData.plan_name || "—"}</p>
                      <p><strong>Date de paiement :</strong> {fmtBr(viewData.paid_date)}</p>
                      <p><strong>Généré le :</strong> {fmtBr(viewData.generated_at)}</p>
                    </div>
                    {viewData.business ? (
                      <div data-testid="client-business-content" className="space-y-5">
                        <div><p className="eyebrow">NOM DU BUSINESS</p><h2>{displayText(viewData.business.business_name)}</h2></div>
                        {viewData.business.pitch && <div><p className="eyebrow">IDÉE</p><p>{displayText(viewData.business.pitch)}</p></div>}
                        {viewData.business.target_client && <div><p className="eyebrow">CLIENT CIBLE</p><p>{displayText(viewData.business.target_client)}</p></div>}
                        {Array.isArray(viewData.business.plan_30_days) && viewData.business.plan_30_days.length > 0 && (
                          <div><p className="eyebrow">PLAN D'ACTION</p>
                            <div className="space-y-2">{viewData.business.plan_30_days.map((d, i) => (
                              <div key={i}><strong>Jour {d.day} : {displayText(d.title)}</strong>
                                <ul style={{ marginLeft: 16, listStyle: "disc" }}>{(d.tasks || []).map((t, j) => <li key={j}>{displayText(t)}</li>)}</ul>
                              </div>))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : <p className="muted" data-testid="client-business-none">Aucun business enregistré</p>}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : tab === "stats" ? (
        <AdminStats password={password} />
      ) : (
      <>
      <div className="admin-heading"><p className="eyebrow">01 — TABLEAU DE BORD</p><h1>Les projets prennent <em>vie.</em></h1></div>
      <div className="admin-stats">
        <div className="admin-stat"><p className="eyebrow">LEADS</p><p className="admin-stat-value" data-testid="admin-leads-count">{leads.length}</p></div>
        <div className="admin-stat"><p className="eyebrow">PAYÉS</p><p className="admin-stat-value" data-testid="admin-paid-count">{paidCount}</p></div>
        <div className="admin-stat"><p className="eyebrow">CONVERSION</p><p className="admin-stat-value accent" data-testid="admin-conversion">{leads.length ? Math.round((paidCount / leads.length) * 100) : 0}%</p></div>
      </div>
      <div className="overflow-x-auto" data-testid="admin-table">
        <table className="admin-table">
          <thead><tr><th>Email</th><th>Date</th><th>Source</th><th>Payé</th><th>Business</th></tr></thead>
          <tbody>
            {leads.map((l) => <tr key={l.id} data-testid={`admin-lead-${l.id}`}><td>{l.email}</td><td className="muted whitespace-nowrap">{new Date(l.created_at).toLocaleString("fr-FR")}</td><td className="muted" data-testid={`admin-lead-source-${l.id}`}>{l.ref || "—"}</td><td><span className={`status-tag ${l.paid ? "paid" : ""}`}>{l.paid ? "Oui" : "Non"}</span></td><td className="muted">{displayText(l.business_name)}</td></tr>)}
            {leads.length === 0 && <tr><td colSpan={5} className="muted" data-testid="admin-empty">Aucun lead pour le moment</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="admin-heading mt-16">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div><p className="eyebrow">02 — AVIS CLIENTS</p><h2>Modération des <em>avis.</em></h2></div>
          <button onClick={deleteDemo} data-testid="admin-delete-demo" className="btn-secondary btn-small">Supprimer les avis d'exemple</button>
        </div>
      </div>
      <div data-testid="admin-reviews">
        {reviews.length === 0 && <p className="muted text-sm mt-4" data-testid="admin-reviews-empty">Aucun avis pour le moment.</p>}
        {reviews.map((r) => (
          <div key={r.id} className="admin-review" data-testid={`admin-review-${r.id}`}>
            <div className="admin-review-top">
              <div className="flex items-center gap-3">
                <span className="review-stars" aria-label={`${r.rating} sur 5`}>
                  {Array.from({ length: 5 }).map((_, s) => <Star key={s} size={13} className={s < r.rating ? "star-on" : "star-off"} fill={s < r.rating ? "currentColor" : "none"} strokeWidth={s < r.rating ? 0 : 1.5} />)}
                </span>
                <strong>{r.name}</strong>
                <span className={`status-tag ${STATUS_CLASS[r.status] || ""}`} data-testid={`admin-review-status-${r.id}`}>{STATUS_LABEL[r.status] || r.status}</span>
                {r.is_demo && <span className="status-tag demo">Exemple</span>}
              </div>
              <div className="admin-review-actions">
                <button onClick={() => moderate(r.id, "approve")} data-testid={`admin-review-approve-${r.id}`} className="btn-secondary btn-small">Approuver</button>
                <button onClick={() => moderate(r.id, "refuse")} data-testid={`admin-review-refuse-${r.id}`} className="btn-secondary btn-small">Refuser</button>
              </div>
            </div>
            {r.comment && <p className="muted text-sm mt-3">« {displayText(r.comment)} »</p>}
            <p className="mono muted text-[10px] mt-2">{new Date(r.created_at).toLocaleString("fr-FR")}</p>
          </div>
        ))}
      </div>
      </>
      )}
    </main>
  );
}
